// errorReporter — cầu nối lỗi FE của kernel → Sentry/GlitchTip của HOST.
//
// ⭐ VÌ SAO PHẢI LÀ CÁI MÓC, KHÔNG PHẢI `import * as Sentry from '@sentry/react'`:
// cnv-ui KHÔNG có @sentry/* trong dependency (grep '@sentry' toàn repo = 0 kết quả) và
// mỗi host wire khác nhau — SaaS có `cnvwork-saas/admin/src/sentry.js` (@sentry/react,
// ingest qua hub.cnvwork.com/monitor), Core (attendance-app) HÔM NAY CHƯA CÓ Sentry nào.
// Kernel mà import thẳng thì Core build fail ngay vì thiếu package. Nên kernel chỉ giữ
// chỗ cắm; host nào có thì đăng ký, host nào không thì mọi thứ no-op im lặng.
//
// ⚠️ SỰ CỐ 18/08/2026 (lý do file này ra đời): ErrorBoundary dùng chung chỉ `console.error`
// → báo cáo CRM lỗi cả buổi mà GlitchTip trống trơn, không ai biết; phải SSH + đọc DB +
// hỏi sếp Phú mới lần ra. Console của trình duyệt KHÁCH thì mình không đọc được — lỗi FE
// không bắn về server = mù hoàn toàn.
//
// Thứ tự tìm nơi nhận (dừng ở cái đầu tiên có):
//   1. Hàm host đăng ký qua setErrorReporter()  — đường chính.
//   2. window.Sentry?.captureException          — host nhúng Sentry dạng global/CDN.
//   3. Không có gì → no-op. KHÔNG throw, KHÔNG console thêm: lớp báo lỗi mà tự ném lỗi
//      thì nó phá đúng cái nó đang cứu (ErrorBoundary gọi nó trong componentDidCatch).

/** Hàm host đăng ký. Chữ ký: (error, { tags, extra, level }) => void */
let hostReporter = null;

/**
 * Host cắm nơi nhận lỗi vào kernel. Gọi MỘT LẦN lúc boot, sau khi Sentry.init().
 * SaaS: xem `cnvwork-saas/admin/src/sentry.js`.
 *
 * @param {(error: unknown, ctx: {tags?: Record<string,string>, extra?: Record<string,unknown>, level?: string}) => void | null} fn
 * @returns {() => void} hàm gỡ đăng ký (dùng trong test)
 */
export function setErrorReporter(fn) {
  hostReporter = typeof fn === 'function' ? fn : null;
  return () => { hostReporter = null; };
}

/** Có nơi nhận lỗi chưa? (dùng để quyết định có nên log thêm ra console hay không) */
export function hasErrorReporter() {
  if (hostReporter) return true;
  try {
    return typeof window !== 'undefined' && typeof window.Sentry?.captureException === 'function';
  } catch {
    return false;
  }
}

/**
 * Bắn lỗi về hệ giám sát của host. An toàn tuyệt đối: mọi lỗi bên trong đều bị nuốt.
 *
 * @param {unknown} error   lỗi gốc — GIỮ NGUYÊN object để Sentry còn stack/type mà gom nhóm
 * @param {{tags?: Record<string,string>, extra?: Record<string,unknown>, level?: string}} [ctx]
 * @returns {boolean} true nếu đã đẩy đi được
 */
export function reportError(error, ctx = {}) {
  try {
    if (hostReporter) { hostReporter(error, ctx); return true; }
    const capture = typeof window !== 'undefined' ? window.Sentry?.captureException : null;
    if (typeof capture === 'function') { capture.call(window.Sentry, error, ctx); return true; }
  } catch { /* never block — xem ghi chú đầu file */ }
  return false;
}

// Đoạn đường dẫn là ĐỊNH DANH (id số, uuid, hash dài) → thay bằng ':id'. Không chuẩn hoá thì
// mỗi deal/lead là một giá trị tag riêng → Sentry đội hàng nghìn tag, gom nhóm vô nghĩa.
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24,})$/i;

/**
 * Route hiện tại, đã chuẩn hoá, dùng làm tag Sentry.
 * CỐ Ý bỏ query string: có thể chứa token/PII (cùng luật với beforeSend của host).
 * Đọc window.location thay vì hook router để dùng được trong class ErrorBoundary.
 */
export function currentRouteTag() {
  try {
    const path = window.location.pathname || '/';
    return path.split('/').map((seg) => (ID_SEGMENT.test(seg) ? ':id' : seg)).join('/') || '/';
  } catch {
    return 'unknown';
  }
}
